import React, { useState, useEffect } from "react";
import {
  StaffListItem,
  StaffFilters,
} from "../../types/staff.types";
import { ClinicStaffService } from "../../services/api/clinic.staff.service";
import { useAuth } from "../../context/AuthContext";
import { StaffFiltersBar } from "./StaffFiltersBar";
import { StaffListingTable } from "./StaffListingTable";
import { StaffDetailsComponent } from "./StaffDetailsComponent";

export const ClinicStaffPage: React.FC = () => {
  const { user } = useAuth();
  const clinicId = user?.role?.toLowerCase() === "clinic" ? user?.id : undefined;

  const [staffList, setStaffList] = useState<StaffListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItemsCount, setTotalItemsCount] = useState(0);

  const [filters, setFilters] = useState<StaffFilters>({
    searchText: "",
    department: "",
    employeeStatus: "",
    employmentType: "",
  });

  // Details in-page state
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);

  useEffect(() => {
    fetchStaffList();
  }, [currentPage, pageSize, filters, clinicId]);

  const fetchStaffList = async () => {
    try {
      setIsLoading(true);
      const res = await ClinicStaffService.getStaffListing({
        page: currentPage,
        pageSize,
        clinicId,
        department: filters.department || undefined,
        designation: filters.designation || undefined,
        employeeStatus: filters.employeeStatus || undefined,
        employmentType: filters.employmentType || undefined,
        searchText: filters.searchText || undefined,
      });

      if (res.success && res.data) {
        setStaffList(res.data);
        if (res.pagination) {
          setTotalPages(res.pagination.totalPages || 1);
          setTotalItemsCount(res.pagination.totalItemsCount || res.data.length);
        }
      } else {
        setStaffList([]);
      }
    } catch (error) {
      console.error("Error fetching clinic staff:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetFilters = () => {
    setFilters({
      searchText: "",
      department: "",
      employeeStatus: "",
      employmentType: "",
    });
    setCurrentPage(1);
  };

  const handleViewStaff = (staff: StaffListItem) => {
    setSelectedStaffId(staff.id);
  };

  const handleCloseDetails = () => {
    setSelectedStaffId(null);
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      {selectedStaffId ? (
        /* In-page Clinic Read-Only Staff Details View */
        <StaffDetailsComponent
          staffId={selectedStaffId}
          isUpdate={false}
          readOnly={true}
          onClose={handleCloseDetails}
        />
      ) : (
        /* Staff Listing View */
        <>
          {/* Top Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Staff</h1>
              <p className="text-xs text-gray-500 mt-0.5">
                View your clinic staff members, roles, and profiles
              </p>
            </div>
          </div>

          {/* Filters Bar */}
          <StaffFiltersBar
            filters={filters}
            onFilterChange={(f) => {
              setFilters(f);
              setCurrentPage(1);
            }}
            onReset={handleResetFilters}
          />

          {/* Table Listing (Read-only for Clinic) */}
          <StaffListingTable
            staffList={staffList}
            isLoading={isLoading}
            currentPage={currentPage}
            pageSize={pageSize}
            totalItemsCount={totalItemsCount}
            totalPages={totalPages}
            readOnly={true}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            onViewDetails={handleViewStaff}
          />
        </>
      )}
    </div>
  );
};
